import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { analytics } from '../lib/analytics';

/**
 * /links — CreateCollect's Instagram bio landing page.
 *
 * Unlisted: intentionally not linked from nav, footer, or any other route.
 * Mobile-first phone-width layout (max ~420px) on all viewports; centered
 * inside a Paper background on larger screens. Uses only existing brand
 * tokens (background, foreground, primary, accent, warning, border) and
 * existing font families (heading = Space Grotesk, body = Work Sans,
 * mono = Courier Prime).
 *
 * Data source: /api/links (server-side Airtable proxy). Filters Live=true,
 * sorted by Order asc. Airtable edits appear within ~60s (server cache).
 */

const CC_URL = 'https://createcollect.com';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tag every outbound link so IG-driven traffic shows up cleanly in analytics.
const withUtm = (url) => {
  try {
    const u = new URL(url);
    if (!u.searchParams.has('utm_source')) u.searchParams.set('utm_source', 'links');
    return u.toString();
  } catch {
    return url;
  }
};

// Fire-and-forget tap tracking — never blocks navigation.
const trackTap = (tile) => {
  analytics.linksTileTap(tile.post_label || tile.link_label);
  axios.post(`${API}/links/tap`, {
    tile_id: tile.id,
    post_label: tile.post_label || '',
    link_label: tile.link_label || '',
    destination_url: tile.destination_url || '',
  }).catch(() => {});
};

const LinksHeader = () => (
  <header
    className="w-full flex items-center justify-center pt-8 pb-6"
    data-testid="links-header"
  >
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center font-heading font-bold tracking-tight w-11 h-11 rounded-md bg-primary text-primary-foreground text-lg"
      data-testid="links-monogram"
    >
      CC
    </span>
  </header>
);

const VisitCTA = () => (
  <a
    href={withUtm(CC_URL)}
    target="_blank"
    rel="noopener noreferrer"
    className="block w-full rounded-md bg-primary text-primary-foreground font-heading font-bold tracking-tight text-lg sm:text-xl px-6 py-5 text-center border-2 border-foreground shadow-[4px_4px_0_0_hsl(var(--foreground))] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_0_hsl(var(--foreground))] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-transform"
    data-testid="links-visit-cc-cta"
  >
    Visit CreateCollect
    <span
      aria-hidden="true"
      className="font-mono ml-2 text-base align-middle"
    >
      →
    </span>
  </a>
);

const Tile = ({ tile, accentIsWarning }) => {
  const { thumbnail_url, destination_url, post_label, link_label } = tile;
  const fallbackBg = accentIsWarning ? 'bg-warning' : 'bg-accent';

  return (
    <a
      href={withUtm(destination_url)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackTap(tile)}
      className="block relative w-full aspect-square overflow-hidden bg-secondary active:opacity-80 transition-opacity"
      data-testid={`links-tile-${tile.id}`}
      aria-label={link_label || post_label || 'Link tile'}
    >
      {thumbnail_url ? (
        <img
          src={thumbnail_url}
          alt={post_label || link_label || ''}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      ) : (
        <div
          className={`w-full h-full ${fallbackBg}`}
          aria-hidden="true"
          data-testid={`links-tile-fallback-${tile.id}`}
        />
      )}
    </a>
  );
};

const LoadingSkeleton = () => (
  <div className="grid grid-cols-3 gap-px" data-testid="links-loading">
    {Array.from({ length: 9 }).map((_, i) => (
      <div
        key={i}
        className="aspect-square bg-secondary animate-pulse"
      />
    ))}
  </div>
);

const EmptyState = () => (
  <div
    className="border-2 border-dashed border-foreground/40 rounded-md py-12 px-6 text-center"
    data-testid="links-empty"
  >
    <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">
      No links yet
    </p>
    <p className="font-body text-sm text-foreground/70">
      New posts show up here as they go live.
    </p>
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div
    className="border-2 border-foreground rounded-md py-8 px-5 text-center bg-warning"
    data-testid="links-error"
  >
    <p className="font-heading font-bold text-lg text-foreground mb-1">
      Couldn&apos;t load links
    </p>
    <p className="font-body text-sm text-foreground/80 mb-4">
      Give it another shot in a moment.
    </p>
    <button
      onClick={onRetry}
      className="font-mono text-xs uppercase tracking-widest px-4 py-2 border-2 border-foreground bg-background text-foreground hover:bg-foreground hover:text-background transition-colors rounded-md"
      data-testid="links-retry-button"
    >
      Try again
    </button>
  </div>
);

export default function Links() {
  const [tiles, setTiles] = useState(null); // null = loading, [] = empty, array = data
  const [error, setError] = useState(false);

  const load = async () => {
    setError(false);
    setTiles(null);
    try {
      const { data } = await axios.get(`${API}/links`);
      setTiles(Array.isArray(data?.tiles) ? data.tiles : []);
    } catch (e) {
      setError(true);
    }
  };

  useEffect(() => {
    document.title = 'CreateCollect · Links';
    load();
  }, []);

  // Dominant accent: Riso Blue (accent token). Flash Yellow (warning) used
  // sparingly on error surface only, with foreground/ink text.
  return (
    <div
      className="min-h-screen w-full bg-background text-foreground"
      data-testid="links-page"
    >
      <div className="mx-auto w-full max-w-[420px] px-5 pb-16">
        <LinksHeader />

        <section className="mb-8" data-testid="links-cta-section">
          <VisitCTA />
          <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground text-center">
            Invoicing for creatives
          </p>
        </section>

        <div className="flex items-baseline justify-between mb-4">
          <h1
            className="font-heading font-bold text-xl tracking-tight"
            data-testid="links-heading"
          >
            Latest
          </h1>
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            @createcollect
          </span>
        </div>

        {error && <ErrorState onRetry={load} />}
        {!error && tiles === null && <LoadingSkeleton />}
        {!error && Array.isArray(tiles) && tiles.length === 0 && <EmptyState />}
        {!error && Array.isArray(tiles) && tiles.length > 0 && (
          <div
            className="grid grid-cols-3 gap-px"
            data-testid="links-grid"
          >
            {tiles.map((t) => (
              <Tile key={t.id} tile={t} accentIsWarning={false} />
            ))}
          </div>
        )}

        <footer className="mt-12 text-center" data-testid="links-footer">
          <a
            href={withUtm(CC_URL)}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
            data-testid="links-footer-link"
          >
            createcollect.com
          </a>
        </footer>
      </div>
    </div>
  );
}
